import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminAPI } from '../config/api';
import AdminSidebar from '../components/AdminSidebar';
import { showAppToast } from '../utils/notifications';

const emptyMessageForm = {
  name: '',
  email: '',
  city: '',
  country: '',
  phone: '',
  message: '',
};

function messageToForm(message) {
  return {
    name: message?.name || '',
    email: message?.email || '',
    city: message?.city || '',
    country: message?.country || '',
    phone: message?.phone || '',
    message: message?.message || '',
  };
}

function AdminMessageForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [formData, setFormData] = useState(emptyMessageForm);

  useEffect(() => {
    if (!isEditing) return;

    let isMounted = true;
    const fetchMessage = async () => {
      setLoading(true);
      try {
        const response = await adminAPI.getMessage(id);
        if (!isMounted) return;
        const message = response.data;
        setSelectedMessage(message);
        setFormData(messageToForm(message));
      } catch (error) {
        showAppToast({ message: error.response?.data?.message || 'Failed to load selected message', type: 'error' });
        navigate('/admin/messages', { replace: true });
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchMessage();
    return () => {
      isMounted = false;
    };
  }, [id, isEditing, navigate]);

  const pageCopy = useMemo(() => {
    if (isEditing) {
      return {
        eyebrow: 'Edit Message',
        title: 'Update customer enquiry details',
        description: `Refine the saved enquiry from ${selectedMessage?.name || 'the selected customer'}.`,
        submit: 'Save Message',
      };
    }

    return {
      eyebrow: 'Create Message',
      title: 'Add a customer enquiry record',
      description: 'Capture a request manually so support history stays complete.',
      submit: 'Create Message',
    };
  }, [isEditing, selectedMessage]);

  const handleFormChange = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const submitMessage = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      if (isEditing) {
        await adminAPI.updateMessage(id, formData);
        showAppToast({ message: 'Message updated successfully', type: 'success', duration: 2400 });
      } else {
        await adminAPI.createMessage(formData);
        showAppToast({ message: 'Message created successfully', type: 'success', duration: 2400 });
      }
      navigate('/admin/messages');
    } catch (error) {
      showAppToast({ message: error.response?.data?.message || 'Failed to save message', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-layout">
        <AdminSidebar />
        <div className="admin-content px-4 py-16">
          <div className="admin-route-loading-card card-luxury" role="status" aria-live="polite">
            <i className="bi bi-chat-left-text-fill" aria-hidden="true" />
            <strong>Loading message</strong>
            <p className="text-secondary-theme">Opening the selected enquiry editor...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content px-4 py-8">
        <div className="mx-auto max-w-5xl">
          <section className="admin-header admin-editor-hero card-luxury">
            <div>
              <p className="text-accent">{pageCopy.eyebrow}</p>
              <h1 className="section-title text-4xl text-primary-theme">{pageCopy.title}</h1>
              <p className="text-secondary-theme">{pageCopy.description}</p>
            </div>
            <button type="button" className="btn-luxury-outline" onClick={() => navigate('/admin/messages')}>
              <i className="bi bi-arrow-left" aria-hidden="true" />
              Back to Messages
            </button>
          </section>

          <form className="admin-form-card card-luxury admin-message-form" onSubmit={submitMessage} aria-label="Admin message form">
            <div className="admin-form-grid">
              <label className="admin-field">
                <span><i className="bi bi-person" aria-hidden="true" /> Name</span>
                <input value={formData.name} onChange={(event) => handleFormChange('name', event.target.value)} required />
              </label>
              <label className="admin-field">
                <span><i className="bi bi-envelope" aria-hidden="true" /> Email</span>
                <input type="email" value={formData.email} onChange={(event) => handleFormChange('email', event.target.value)} required />
              </label>
              <label className="admin-field">
                <span><i className="bi bi-telephone" aria-hidden="true" /> Phone</span>
                <input value={formData.phone} onChange={(event) => handleFormChange('phone', event.target.value)} required />
              </label>
              <label className="admin-field">
                <span><i className="bi bi-geo-alt" aria-hidden="true" /> City</span>
                <input value={formData.city} onChange={(event) => handleFormChange('city', event.target.value)} required />
              </label>
              <label className="admin-field">
                <span><i className="bi bi-flag" aria-hidden="true" /> Country</span>
                <input value={formData.country} onChange={(event) => handleFormChange('country', event.target.value)} required />
              </label>
              <label className="admin-field admin-field--wide">
                <span><i className="bi bi-chat-left-text" aria-hidden="true" /> Message</span>
                <textarea value={formData.message} onChange={(event) => handleFormChange('message', event.target.value)} required />
              </label>
            </div>
            <div className="admin-editor-actions">
              <button type="submit" className="btn-luxury admin-form-submit" disabled={saving}>
                <i className="bi bi-check2-circle" aria-hidden="true" />
                {saving ? 'Saving...' : pageCopy.submit}
              </button>
              <button type="button" className="btn-luxury-outline" onClick={() => navigate('/admin/messages')} disabled={saving}>
                <i className="bi bi-x-lg" aria-hidden="true" />
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AdminMessageForm;
